import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { TermDto, TermType } from '@/types/dtos/events/term.dto.ts';
import { GET_TERMS_QUERY_KEY, GET_PUBLISHED_VERSIONS_QUERY_KEY } from '../../queries/events/use-get-terms.ts';

export const usePublishTermMutation = (options?: { onSuccess?: (data: TermDto) => void }) => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, termType }: { eventId: string; termType: TermType }) => {
      const token = await getToken();
      return publishTerm({ eventId, termType, token: token ?? '' });
    },
    onSuccess: async (data, variables) => {
      // Invalidate both PLAN and ORGANIZE terms queries
      await queryClient.invalidateQueries({
        queryKey: [GET_TERMS_QUERY_KEY, variables.eventId],
      });
      await queryClient.invalidateQueries({
        queryKey: [GET_PUBLISHED_VERSIONS_QUERY_KEY, variables.eventId, variables.termType],
      });
      options?.onSuccess?.(data);
    },
  });
};

const publishTerm = async ({
  eventId,
  termType,
  token,
}: {
  eventId: string;
  termType: TermType;
  token: string;
}): Promise<TermDto> => {
  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/terms/${termType}/publish`;
  return sendJSONRequest({
    url,
    method: METHOD.POST,
    body: {},
    token,
  });
};
